import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TextField,
  Button,
  CircularProgress,
  Grid,
  Typography,
  Box,
  Snackbar,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL;

const IndustryPage = () => {
  const [industryTypes, setIndustryTypes] = useState([]); // Store all industry types
  const [industryName, setIndustryName] = useState(''); // For editing
  const [newIndustryName, setNewIndustryName] = useState(''); // For editing new name
  const [newIndustryType, setNewIndustryType] = useState(''); // For creating new industry type
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // Fetch all available industry types
  useEffect(() => {
    axios
      .get(`${API_URL}/industry-types/`)
      .then((response) => {
        setIndustryTypes(response.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching industry types:', err);
        setLoading(false);
      });
  }, []);

  // Handle Edit Industry
  const handleEditIndustry = () => {
    if (!industryName || !newIndustryName) {
      setErrorMessage('Both industry name and new name are required.');
      setOpenSnackbar(true);
      return;
    }

    axios
      .post(`${API_URL}/industry-type/edit/`, {
        name: industryName, // Existing industry name
        new_name: newIndustryName, // New industry name
      })
      .then((response) => {
        setIndustryTypes((prev) =>
          prev.map((item) =>
            item.name === industryName ? { ...item, name: newIndustryName } : item
          )
        );
        setNewIndustryName(''); // Reset the input field
        setOpenSnackbar(true);
      })
      .catch((error) => {
        console.error('Error editing industry:', error.response || error);
        setErrorMessage(error.response?.data?.error || 'Failed to edit industry.');
        setOpenSnackbar(true);
      });
  };

  // Handle Delete Industry
  const handleDeleteIndustry = () => {
    if (!industryName) {
      setErrorMessage('Industry name is required to delete.');
      setOpenSnackbar(true);
      return;
    }

    // Confirm before deleting
    if (!window.confirm(`Are you sure you want to delete the industry: ${industryName}?`)) {
      return;
    }

    axios
      .post(`${API_URL}/industry-type/delete/`, { name: industryName })
      .then((response) => {
        setIndustryTypes((prev) => prev.filter((item) => item.name !== industryName));
        setIndustryName(''); // Reset the selection
        setOpenSnackbar(true);
      })
      .catch((error) => {
        console.error('Error deleting industry:', error.response || error);
        setErrorMessage(error.response?.data?.error || 'Failed to delete industry.');
        setOpenSnackbar(true);
      });
  };

  // Handle Add Industry Type
  const handleAddIndustryType = () => {
    if (!newIndustryType) {
      setErrorMessage('Industry name is required to create.');
      setOpenSnackbar(true);
      return;
    }

    axios
      .post(`${API_URL}/industry-type/create/`, { name: newIndustryType }, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((response) => {
        setIndustryTypes((prev) => [...prev, { name: newIndustryType }]);
        setNewIndustryType(''); // Reset the input field
        setOpenSnackbar(true);
      })
      .catch((error) => {
        console.error('Error creating industry:', error.response || error);
        setErrorMessage(error.response?.data?.error || 'Failed to create industry type.');
        setOpenSnackbar(true);
      });
  };

  // Loading spinner
  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Industry Types Management
      </Typography>

      <Grid container spacing={2}>
        {/* Dropdown or list of industry types */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Select Industry Type"
            select
            fullWidth
            value={industryName}
            onChange={(e) => setIndustryName(e.target.value)}
            SelectProps={{
              native: true,
            }}
          >
            <option value="" disabled>
              Select Industry
            </option>
            {industryTypes.map((industry) => (
              <option key={industry.name} value={industry.name}>
                {industry.name}
              </option>
            ))}
          </TextField>
        </Grid>

        {/* Input for editing */}
        <Grid item xs={12} md={6}>
          <TextField
            label="New Industry Type Name"
            fullWidth
            value={newIndustryName}
            onChange={(e) => setNewIndustryName(e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={handleEditIndustry}>
            Edit Industry Type
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Button variant="outlined" color="primary" onClick={handleDeleteIndustry}>
            Delete Industry Type
          </Button>
        </Grid>

        {/* Input for adding a new industry */}
        <Grid item xs={12} md={6}>
          <TextField
            label="New Industry Type"
            fullWidth
            value={newIndustryType}
            onChange={(e) => setNewIndustryType(e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" color="success" onClick={handleAddIndustryType}>
            Add Industry Type
          </Button>
        </Grid>
      </Grid>

      {/* Snackbar for success or error */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        message={errorMessage || 'Success!'}
      />
    </Box>
  );
};

export default IndustryPage;
